import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { userMessageFor } from '@/api/errors'

/**
 * A query that opts out of the global handler owns its own error reporting.
 */
declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { skipGlobalErrorHandler?: boolean }
    mutationMeta: { skipGlobalErrorHandler?: boolean }
  }
}

function report(error: unknown, skip: boolean | undefined): void {
  if (skip === true) return

  const { title, description } = userMessageFor(error)
  toast.error(title, { description })
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // The websocket pushes changes, so refetching on focus is redundant
        // noise once the realtime layer lands in M4.
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 30_000,
      },
    },
    // Reported once here rather than at each call site. A failed request the user
    // provoked has to say something, and the alternative -- every hook remembering
    // to catch -- is how a silent failure gets shipped.
    queryCache: new QueryCache({
      onError: (error, query) => {
        report(error, query.meta?.skipGlobalErrorHandler)
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        report(error, mutation.meta?.skipGlobalErrorHandler)
      },
    }),
  })
}
